/**
 * Data layer for `custom-payout-rules-per-influencer-tier`. Thin typed fetch wrappers over /api/v1/custom-payout-rules-per-influencer-tier.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomPayoutRulesPerInfluencerTierRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomPayoutRulesPerInfluencerTierResponse {
	count: number;
	items: CustomPayoutRulesPerInfluencerTierRow[];
}

export interface CreateCustomPayoutRulesPerInfluencerTierBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-payout-rules-per-influencer-tier";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomPayoutRulesPerInfluencerTierResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomPayoutRulesPerInfluencerTierResponse>(res);
}

export async function create(body: CreateCustomPayoutRulesPerInfluencerTierBody): Promise<CustomPayoutRulesPerInfluencerTierRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomPayoutRulesPerInfluencerTierRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customPayoutRulesPerInfluencerTierQueryKeys = {
	all: ["custom-payout-rules-per-influencer-tier"] as const,
	list: () => [...customPayoutRulesPerInfluencerTierQueryKeys.all] as const,
	detail: (id: string) => [...customPayoutRulesPerInfluencerTierQueryKeys.all, id] as const,
};
